const variantPriceCache = new Map();
let activeRingSize = null;

/* ------------------------------------------------------------------ *
 * Variant-sync watchdog (2026-08-04; rebuilt 2026-08-08 after the
 * carat-loss incident): selecting a carat/metal updates the buttons
 * optimistically, then relies on the theme's section-render fetch to
 * refresh the price and the product form. If that fetch fails or lands
 * out of order, Add to Cart can hold the WRONG variant. The watchdog
 * verifies the form caught up and repairs it.
 *
 * HARD RULE (2026-08-08): truth is THE SELECTION THE CUSTOMER SEES -
 * the selected carat button + the checked metal radio - resolved
 * against the product's own variant list (/products/<handle>.js).
 * Never a per-input data-variant-id snapshot: those are stamped at the
 * last section render, so after a carat change the metal radios still
 * carry the OLD carat's ids, and force-writing one sold a 2 ct as a
 * 3 ct. Deriving from the visible selection is idempotent and cannot
 * go stale.
 * ------------------------------------------------------------------ */
let pendingVariantSync = 0;
let watchdogRefiring = false;

function mainProductFormInput() {
  return (
    document.querySelector('product-form-component form input[name="id"]') ||
    document.querySelector('.product-information form[action*="/cart/add"] input[name="id"]')
  );
}

function moneyWhole(cents) {
  return '$' + Math.round(cents / 100).toLocaleString('en-US');
}

function applyHardPriceSync(variant) {
  /* price hierarchy (design review P2, 2026-08-23): this builder MUST mirror the
     Liquid structure in beylora-c-pdp-buy.liquid (price-now/amount/ctx + wasline).
     It rebuilds the row on every sync, so a divergence here silently reverts the
     rendered hierarchy (caught on the sandbox 2026-08-23). */
  const head = document.querySelector('.beylora-c-pdp-buy__price');
  if (head && Number.isFinite(variant.price)) {
    head.textContent = '';
    const now = document.createElement('span');
    now.className = 'beylora-c-pdp-buy__price-now';
    const amt = document.createElement('span');
    amt.className = 'beylora-c-pdp-buy__amount';
    amt.textContent = moneyWhole(variant.price);
    now.appendChild(amt);
    now.appendChild(document.createTextNode(' '));
    const ctx = document.createElement('span');
    ctx.className = 'beylora-c-pdp-buy__ctx';
    ctx.textContent = 'the complete ring';
    now.appendChild(ctx);
    head.appendChild(now);
    if (Number.isFinite(variant.compare_at_price) && variant.compare_at_price > variant.price) {
      const was = document.createElement('span');
      was.className = 'beylora-c-pdp-buy__wasline';
      const msrp = document.createElement('s');
      msrp.className = 'beylora-c-pdp-buy__msrp';
      msrp.textContent = moneyWhole(variant.compare_at_price);
      was.appendChild(msrp);
      const off = document.createElement('span');
      off.className = 'beylora-c-pdp-buy__off';
      off.textContent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) + '% off';
      was.appendChild(off);
      head.appendChild(was);
    }
  }
  const sticky = document.querySelector('.beylora-c-pdp-sticky__price');
  if (sticky && Number.isFinite(variant.price)) sticky.textContent = moneyWhole(variant.price);
}

let productDataPromise = null;
function productData() {
  if (!productDataPromise) {
    const match = window.location.pathname.match(/\/products\/[^/?#]+/);
    if (!match) return Promise.resolve(null);
    productDataPromise = fetch(`${match[0]}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  return productDataPromise;
}

/* The variant the CUSTOMER has selected right now: carat/origin from the
   is-selected split buttons, metal from the checked material radio, matched
   against the product's real variant list. Returns null (never guesses)
   unless both halves of the selection are present and match a variant. */
async function expectedVariantFromSelection() {
  const product = await productData();
  if (!product || !Array.isArray(product.variants)) return null;

  const splitRoot = document.querySelector('[data-beylora-split-picker]');
  const combo = splitRoot ? currentComboValue(splitRoot) : null;
  const material = selectedMaterialInput(document)?.value || null;
  if (!combo || !material) return null;

  return (
    product.variants.find((variant) => {
      const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      return options.includes(combo) && options.includes(material);
    }) || null
  );
}

function scheduleVariantSync(input) {
  window.clearTimeout(pendingVariantSync);

  const attempt = (round) => {
    pendingVariantSync = window.setTimeout(async () => {
      /* the triggering input may have been superseded or morphed away; the
         selection state itself is re-read fresh every round, so we only
         bail if the input is a radio the user has since unchecked */
      if (input && input.isConnected && input.type === 'radio' && !input.checked) return;

      const expected = await expectedVariantFromSelection();
      if (!expected) return;

      const formInput = mainProductFormInput();
      if (!formInput || formInput.value === String(expected.id)) {
        /* render landed on the right variant; one silent re-verify round
           guards against a stale in-flight render morphing it back */
        if (round < 2) attempt(round + 1);
        return;
      }

      if (round === 0) {
        /* re-fire the render once; the cache-buster gives it a fresh URL */
        watchdogRefiring = true;
        (input || formInput).dispatchEvent(new Event('change', { bubbles: true }));
        watchdogRefiring = false;
        attempt(1);
        return;
      }

      /* the page must never sell a ring that differs from the visible
         selection - force the money-critical state onto it */
      formInput.value = String(expected.id);
      const installments = document.querySelector('#beylora-installments-form input[name="id"]');
      if (installments) installments.value = String(expected.id);
      applyHardPriceSync(expected);
      if (round < 2) attempt(round + 1);
    }, 1600);
  };

  attempt(0);
}

function installVariantPickerCacheBuster() {
  const VariantPicker = customElements.get('variant-picker');

  if (!VariantPicker?.prototype?.buildRequestUrl) {
    window.setTimeout(installVariantPickerCacheBuster, 50);
    return;
  }

  if (VariantPicker.prototype.buildRequestUrl.__beyloraDynamicPricePatched) return;

  const originalBuildRequestUrl = VariantPicker.prototype.buildRequestUrl;

  VariantPicker.prototype.buildRequestUrl = function buildBeyloraRequestUrl(...args) {
    const requestUrl = originalBuildRequestUrl.apply(this, args);
    if (!this.querySelector('[data-beylora-split-picker]')) return requestUrl;

    const url = new URL(requestUrl, window.location.origin);
    url.searchParams.set('beylora_variant_refresh', String(Date.now()));
    return `${url.pathname}${url.search}`;
  };

  VariantPicker.prototype.buildRequestUrl.__beyloraDynamicPricePatched = true;
}

function formatDelta(deltaInCents) {
  if (deltaInCents === 0) return 'Same price';

  const amount = Math.abs(deltaInCents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  return `${deltaInCents > 0 ? '+' : '-'}${amount}`;
}

function relativeLabel(optionPrice, selectedPrice, selected) {
  /* Delta-plus-absolute (Joe ruling 2026-08-10, supersedes the 2026-08-09
     absolute-only ruling): the CHANGE from the current selection leads and the
     full resulting price sits small beneath, so a negative delta can never
     read as a discount without its context. Numbers only: safe as HTML. */
  /* Complete-ring price first (Joe, 2026-09-29): the full ring price leads so no
     tile reads as a diamond add-on; the change from the current pick sits small
     beneath, which still keeps a negative change from reading as a discount. */
  if (!Number.isFinite(optionPrice) || optionPrice <= 0) return selected ? 'Selected' : '';
  const lead = moneyWhole(optionPrice);
  if (selected) return lead + '<small class="beylora-option-abs">Selected</small>';
  if (!Number.isFinite(selectedPrice) || selectedPrice <= 0) return lead;
  return lead + '<small class="beylora-option-abs">' + formatDelta(optionPrice - selectedPrice) + '</small>';
}

function selectedButton(root, selector, fallback) {
  return root.querySelector(`${selector}.is-selected`) || root.querySelector(selector) || fallback;
}

function syncPressed(root, selector, activeButton) {
  root.querySelectorAll(selector).forEach((button) => {
    const selected = button === activeButton;
    button.classList.toggle('is-selected', selected);
    button.setAttribute('aria-pressed', selected ? 'true' : 'false');
  });
}

function priceFromElement(element) {
  if (!element) return Number.NaN;

  const price = rawPriceFromElement(element);
  if (element.dataset.variantId && price >= 0 && price < 10000) return Number.NaN;
  return Number.isFinite(price) ? price : Number.NaN;
}

function rawPriceFromElement(element) {
  if (!element) return Number.NaN;

  const price = Number(element.dataset.variantPrice || element.dataset.beyloraMaterialPrice);
  return Number.isFinite(price) ? price : Number.NaN;
}

function priceNeedsHydration(element) {
  if (!element?.dataset.variantId) return false;

  const price = rawPriceFromElement(element);
  return !Number.isFinite(price) || price < 10000;
}

function selectedVariantFromScript(scope) {
  const text = scope.querySelector('script[type="application/json"]')?.textContent;
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (_error) {
    return null;
  }
}

function currentComboValue(root) {
  const selectedOrigin =
    root.querySelector('[data-beylora-origin-choice].is-selected')?.dataset.origin || 'Lab-Grown Diamond';
  const selectedCarat = root.querySelector('[data-beylora-carat-choice].is-selected')?.dataset.carat || '2';

  return `${selectedOrigin} / ${selectedCarat} ct`;
}

function comboInputFor(root, comboValue) {
  return root.querySelector(`.beylora-native-combo input[type="radio"][value="${CSS.escape(comboValue)}"]`);
}

function selectedComboInput(root) {
  return comboInputFor(root, currentComboValue(root)) || root.querySelector('.beylora-native-combo input[type="radio"]:checked');
}

function selectedMaterialInput(scope) {
  const materialInputs = Array.from(
    scope.querySelectorAll('.beylora-material-choice input[type="radio"][data-beylora-material-price]')
  );

  return materialInputs.find((input) => input.checked || input.dataset.currentChecked === 'true') || null;
}

function selectedNativeRingSizeInput(scope) {
  const inputs = Array.from(scope.querySelectorAll('input[type="radio"]'));
  return (
    inputs.find(
      (input) =>
        input.name?.toLowerCase().includes('ring size') &&
        (input.checked || input.dataset.currentChecked === 'true')
    ) || null
  );
}

function selectedRingSize(scope) {
  const selectedButton = scope.querySelector('[data-beylora-ring-size-choice].is-selected');
  if (selectedButton?.dataset.ringSize) return selectedButton.dataset.ringSize;

  const nativeInput = selectedNativeRingSizeInput(scope);
  if (nativeInput?.value) return nativeInput.value;

  return activeRingSize || '';
}

function syncRingSizeProperty(scope = document, explicitSize = null) {
  const sourceScope = scope instanceof Document ? document : scope;
  const scopedInputs = Array.from(sourceScope.querySelectorAll?.('[data-beylora-ring-size-property-input]') || []);
  const allInputs = Array.from(document.querySelectorAll('[data-beylora-ring-size-property-input]'));
  const inputs = Array.from(new Set([...scopedInputs, ...allInputs]));

  if (scope instanceof Element && scope.matches('[data-beylora-ring-size-property-input]')) {
    inputs.push(scope);
  }

  const fallbackSize = explicitSize || activeRingSize || selectedRingSize(sourceScope);
  activeRingSize = fallbackSize;
  if (!fallbackSize) {
    inputs.forEach((input) => { input.value = ''; });
    return;
  }

  document.querySelectorAll('[data-beylora-ring-size-property]').forEach((group) => {
    const matchingButton = group.querySelector(
      `[data-beylora-ring-size-choice][data-ring-size="${CSS.escape(fallbackSize)}"]`
    );
    if (matchingButton) syncPressed(group, '[data-beylora-ring-size-choice]', matchingButton);
  });

  inputs.forEach((input) => {
    input.value = fallbackSize;
  });
}

function currentVariantPrice(scope) {
  const materialPrice = priceFromElement(selectedMaterialInput(scope));
  if (Number.isFinite(materialPrice)) return materialPrice;

  const splitPicker = scope.querySelector('[data-beylora-split-picker]');
  const comboPrice = priceFromElement(selectedComboInput(splitPicker || scope));
  if (Number.isFinite(comboPrice)) return comboPrice;

  const selectedVariant = selectedVariantFromScript(scope.closest?.('variant-picker') || scope);
  const selectedVariantPrice = Number(selectedVariant?.price);
  return Number.isFinite(selectedVariantPrice) ? selectedVariantPrice : Number.NaN;
}

async function hydrateVariantPrice(element) {
  const variantId = element?.dataset.variantId;
  if (!variantId || !priceNeedsHydration(element)) return priceFromElement(element);

  if (!variantPriceCache.has(variantId)) {
    variantPriceCache.set(
      variantId,
      fetch(`/variants/${variantId}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Variant price lookup failed for ${variantId}`);
          return response.json();
        })
        .then((variant) => Number(variant.price))
        .catch(() => Number.NaN)
    );
  }

  const price = await variantPriceCache.get(variantId);
  if (Number.isFinite(price)) {
    element.dataset.variantPrice = String(price);
    if (element.dataset.beyloraMaterialPrice !== undefined) {
      element.dataset.beyloraMaterialPrice = String(price);
    }
  }

  return price;
}

function updateSplitLabels(root) {
  const selectedOrigin =
    root.querySelector('[data-beylora-origin-choice].is-selected')?.dataset.origin || 'Lab-Grown Diamond';
  const selectedCarat = root.querySelector('[data-beylora-carat-choice].is-selected')?.dataset.carat || '2';
  const selectedComboPrice = priceFromElement(comboInputFor(root, `${selectedOrigin} / ${selectedCarat} ct`));
  const selectedPrice = Number.isFinite(selectedComboPrice) ? selectedComboPrice : currentVariantPrice(root);

  root.querySelectorAll('[data-beylora-origin-choice]').forEach((button) => {
    const origin = button.dataset.origin || '';
    const label = button.querySelector('[data-beylora-price-label]');
    if (!label) return;

    const comboInput = comboInputFor(root, `${origin} / ${selectedCarat} ct`);
    const price = priceFromElement(comboInput) || priceFromElement(button);
    label.innerHTML = relativeLabel(price, selectedPrice, origin === selectedOrigin);
  });

  root.querySelectorAll('[data-beylora-carat-choice]').forEach((button) => {
    const carat = button.dataset.carat || '';
    const label = button.querySelector('[data-beylora-price-label]');
    if (!label) return;

    const comboInput = comboInputFor(root, `${selectedOrigin} / ${carat} ct`);
    const price = priceFromElement(comboInput) || priceFromElement(button);
    label.innerHTML = relativeLabel(price, selectedPrice, carat === selectedCarat);
  });
}

function updateMaterialLabels(scope = document) {
  const materialInputs = Array.from(
    scope.querySelectorAll('.beylora-material-choice input[type="radio"][data-beylora-material-price]')
  );
  const selectedInput = selectedMaterialInput(scope);
  const selectedPrice = priceFromElement(selectedInput);

  materialInputs.forEach((input) => {
    const label = input.closest('.beylora-material-choice')?.querySelector('[data-beylora-price-label]');
    if (!label) return;

    label.innerHTML = relativeLabel(priceFromElement(input), selectedPrice, input === selectedInput);
  });
}

function syncAllLabels(scope = document) {
  scope.querySelectorAll('[data-beylora-split-picker]').forEach(updateSplitLabels);
  updateMaterialLabels(scope);
  syncRingSizeProperty(scope);
}

async function hydrateAllPrices(scope = document) {
  const targets = scope.querySelectorAll(
    '[data-beylora-origin-choice][data-variant-id], [data-beylora-carat-choice][data-variant-id], .beylora-native-combo input[data-variant-id], .beylora-material-choice input[data-variant-id]'
  );

  await Promise.allSettled(Array.from(targets, hydrateVariantPrice));
  syncAllLabels(scope);
}

function chooseVariant(button) {
  const root = button.closest('[data-beylora-split-picker]');
  if (!root) return;

  const originButton =
    button.matches('[data-beylora-origin-choice]')
      ? button
      : selectedButton(root, '[data-beylora-origin-choice]', null);
  const caratButton =
    button.matches('[data-beylora-carat-choice]')
      ? button
      : selectedButton(root, '[data-beylora-carat-choice]', null);

  if (!caratButton) return;

  const origin = originButton ? originButton.dataset.origin : 'Lab-Grown Diamond';
  const carat = caratButton.dataset.carat;
  const targetValue = `${origin} / ${carat} ct`;
  const nativeInput = comboInputFor(root, targetValue);

  if (!nativeInput || nativeInput.disabled || nativeInput.getAttribute('aria-disabled') === 'true') return;

  if (originButton) syncPressed(root, '[data-beylora-origin-choice]', originButton);
  syncPressed(root, '[data-beylora-carat-choice]', caratButton);
  updateSplitLabels(root);
  updateMaterialLabels(root.closest('variant-picker') || document);

  if (!nativeInput.checked) {
    nativeInput.checked = true;
    nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
  scheduleVariantSync(nativeInput);

  window.requestAnimationFrame(() => hydrateAllPrices(root.closest('variant-picker') || document));
}

function chooseRingSize(button) {
  const root = button.closest('[data-beylora-ring-size-property]');
  if (!root) return;
  const selectedSize = button.dataset.ringSize || selectedRingSize(root);
  activeRingSize = selectedSize;

  document.querySelectorAll('[data-beylora-ring-size-property]').forEach((group) => {
    const matchingButton = group.querySelector(
      `[data-beylora-ring-size-choice][data-ring-size="${CSS.escape(selectedSize)}"]`
    );
    if (matchingButton) syncPressed(group, '[data-beylora-ring-size-choice]', matchingButton);
  });
  syncRingSizeProperty(document, selectedSize);
  const note = document.querySelector('[data-beylora-size-note]');
  if (note) note.setAttribute('hidden', '');
  document.querySelectorAll('.beylora-size-needed').forEach((g) => g.classList.remove('beylora-size-needed'));
}

function ringSizeGateApplies() {
  return !!document.querySelector('[data-beylora-ring-size-property-input]');
}

function revealRingSizeGate() {
  const note = document.querySelector('[data-beylora-size-note]');
  if (note) note.removeAttribute('hidden');
  const group = document.querySelector('[data-beylora-ring-size-property]');
  if (group) {
    group.classList.add('beylora-size-needed');
    const firstChoice = group.querySelector('[data-beylora-ring-size-choice]');
    if (firstChoice) firstChoice.focus({ preventScroll: false });
    group.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

function syncRingSizeBeforeCartAction(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const addToCartControl = target.closest(
    'button[name="add"], button.add-to-cart-button, .shopify-payment-button__button, form[action*="/cart/add"] button'
  );
  const submittedAddForm =
    event.type === 'submit' && target.matches?.('form[action*="/cart/add"], form[action*="/cart"]');

  if (!addToCartControl && !submittedAddForm) return;

  const size = activeRingSize || selectedRingSize(document);

  /* Phase-0 gate (2026-06-12): a made-to-order, non-resizable ring never enters
     the cart without her size. Block and invite, never guess a default. */
  if (!size && ringSizeGateApplies() && (event.type === 'click' || event.type === 'submit')) {
    event.preventDefault();
    event.stopPropagation();
    /* The theme's document-level click delegate registers BEFORE this gate, so the
       ATC button's success burst (data-added) can already be firing when we block.
       Strip it now and on the next frames - a blocked add must never flash "added". */
    const stripAdded = () => {
      document
        .querySelectorAll('.add-to-cart-button[data-added], [name="add"][data-added]')
        .forEach((b) => b.removeAttribute('data-added'));
    };
    stripAdded();
    requestAnimationFrame(stripAdded);
    window.setTimeout(stripAdded, 60);
    window.setTimeout(stripAdded, 300);
    revealRingSizeGate();
    return;
  }
  if (!size) return;

  syncRingSizeProperty(document, size);
}

document.addEventListener('click', (event) => {
  const ringSizeButton = event.target.closest('[data-beylora-ring-size-choice]');
  if (ringSizeButton) {
    event.preventDefault();
    chooseRingSize(ringSizeButton);
    return;
  }

  const button = event.target.closest('[data-beylora-origin-choice], [data-beylora-carat-choice]');
  if (!button) return;

  event.preventDefault();
  chooseVariant(button);
});

document.addEventListener('change', (event) => {
  if (!(event.target instanceof HTMLInputElement)) return;

  const picker = event.target.closest('variant-picker') || document;

  if (event.target.name?.toLowerCase().includes('ring size')) {
    window.requestAnimationFrame(() => syncRingSizeProperty(picker));
  }

  if (!event.target.closest('.beylora-material-choice')) return;

  if (!watchdogRefiring) scheduleVariantSync(event.target);
  window.requestAnimationFrame(() => hydrateAllPrices(picker));
});

document.addEventListener('pointerdown', syncRingSizeBeforeCartAction, true);
document.addEventListener('click', syncRingSizeBeforeCartAction, true);
document.addEventListener('submit', syncRingSizeBeforeCartAction, true);

document.addEventListener('variant:update', (event) => {
  window.requestAnimationFrame(() => {
    const scope = event.target instanceof Element ? event.target : document;
    syncAllLabels(scope);
    hydrateAllPrices(scope);
    reconcileDisplayedPricesFromTruth(); /* morphs re-inject snapshot prices */
  });
});

/* Price-truth guard (2026-08-10, Joe order: stale-cache displays must self-
   correct): Shopify page snapshots can be up to a day old, so the prices
   BAKED into this HTML may lag an admin price change. One product.js fetch
   (the live truth, already cached by productData) patches every tile's
   embedded price and re-renders labels + the head price line. Runs at boot
   and after every morph, so no interaction path can resurrect stale money. */
async function reconcileDisplayedPricesFromTruth() {
  const product = await productData();
  if (!product || !Array.isArray(product.variants)) return;
  const truth = new Map(product.variants.map((v) => [String(v.id), v]));
  document.querySelectorAll('[data-variant-id]').forEach((el) => {
    const v = truth.get(String(el.dataset.variantId));
    if (!v) return;
    if (el.dataset.variantPrice != null) el.dataset.variantPrice = String(v.price);
    if (el.dataset.beyloraMaterialPrice != null) el.dataset.beyloraMaterialPrice = String(v.price);
  });
  syncAllLabels();
  const expected = await expectedVariantFromSelection();
  if (expected) applyHardPriceSync(expected);
}

installVariantPickerCacheBuster();
activeRingSize = selectedRingSize(document);
syncAllLabels();
hydrateAllPrices();
reconcileDisplayedPricesFromTruth();

/* Replay the click a user made before this module executed (the inline
   early-click queue in variant-main-picker.liquid records it); without
   this, taps during page load on slow connections silently do nothing. */
window.__beyloraSplitReady = true;
const pendingEarlyClick = window.__beyloraPendingSplitClick;
window.__beyloraPendingSplitClick = null;
if (pendingEarlyClick && pendingEarlyClick.isConnected) {
  if (pendingEarlyClick.matches('[data-beylora-ring-size-choice]')) chooseRingSize(pendingEarlyClick);
  else chooseVariant(pendingEarlyClick);
}
